import { IconArrowLeft, IconCrossLarge } from '@tock/icons';
import { IconArrowLeft as IconArrowLeftLegacy, IconClose } from 'ui/icons';
import { IconButton, TockLogo, Typography } from 'omakase';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import {
  // eslint-disable-next-line no-restricted-imports
  DialogTitle as MuiDialogTitle,
  DialogTitleProps as MuiDialogTitleProps,
} from '@material-ui/core';
import { Theme } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import {
  dialogHeading,
  dialogHeadingCenterElement,
  dialogHeadingCenterElementTitle,
  dialogHeadingLeftElementArrow,
  dialogHeadingLeftElementTitle,
  dialogHeadingRightElement,
  dialogHeadingShadow,
} from 'omakase/Dialog/dialogCoreCssDefs';
import React from 'react';
import _ from 'lodash';

type HeadingConfigLeft_Arrow = {
  /**
   * Whether the left element renders a left-aligned title, a logo, or a back arrow icon button
   */
  configType: 'arrow';
  /**
   * A click handler for the left button. Optional; if not specified the onClose() prop for DialogHeading
   * overall will be used instead
   */
  onClickArrow?(): void;
  /**
   * An optional id to assign to the icon. Can be useful for, e.g.,
   * later autofocusing it on showing the dialog.
   */
  id?: string;
};

type HeadingConfigLeft_Logo = {
  configType: 'logo';
  // Possible TODO: add an onClickLogo() subproperty here.
  // But the current TockLogo implementation does not support adding a click handler.
  /**
   * An optional id to assign to the icon. Can be useful for, e.g.,
   * later autofocusing it on showing the dialog.
   */
  id?: string;
};

type HeadingConfigLeft_Title = {
  configType: 'title';
  /**
   * The text to display in the title
   */
  title: string;
  /**
   * An optional id to assign the title element.
   */
  id?: string;
};

export type HeadingConfigLeft =
  | HeadingConfigLeft_Arrow
  | HeadingConfigLeft_Logo
  | HeadingConfigLeft_Title;

type HeadingConfigCenter_Title = {
  /**
   * Whether the center element renders a title or a button
   */
  configType: 'title';
  /**
   * The text to display in the title
   */
  title: string;
};

type HeadingConfigCenter_Button = {
  configType: 'button';
  /**
   * The button to render in the dialog heading
   */
  button: React.ReactNode;
};

export type HeadingConfigCenter = HeadingConfigCenter_Title | HeadingConfigCenter_Button;

type HeadingConfigRight_CloseButton = {
  /**
   * Whether the center element renders a close button
   */
  configType: 'closeButton';
  /**
   * An optional id to assign the close button. Can be useful for, e.g.,
   * later autofocusing it on showing the dialog.
   */
  id?: string;
};

export type HeadingConfigRight = HeadingConfigRight_CloseButton;

type HeadingConfig = {
  /**
   * The element to be placed in the center position of the heading
   */
  center?: HeadingConfigCenter;
  /**
   * The element to be placed in the left position of the heading
   */
  left?: HeadingConfigLeft;
  /**
   * The element to be placed in the right position of the heading
   */
  right?: HeadingConfigRight;
};

export interface DialogHeadingBaseProps {
  /**
   * If true, children will not be rendered in an HTML heading element
   */
  disableTypography?: boolean;
  /**
   * Controls rendering a heading for content, including a title and / or close button.
   */
  headingConfig?: HeadingConfig;
  /**
   * The onClose function for the dialog, or a small object detailing this function and
   * the button id to apply.
   */
  onClose?:
    | (() => void)
    | {
        handler: () => void;
        closeButtonId?: string;
      };
  /**
   * Enable shadow when content is scrolled functionality
   */
  showShadowWhenScrolled?: boolean;
}

export type DialogHeadingProps = DialogHeadingBaseProps & MuiDialogTitleProps;

const DialogHeading = React.forwardRef<HTMLHeadingElement, DialogHeadingProps>(
  (
    {
      children,
      disableTypography = false,
      headingConfig,
      // This makes determining effectiveOnClose and closeButtonId more straightforward:
      onClose = { handler: undefined },
      showShadowWhenScrolled = true,
      id,
      ...rest
    },
    ref
  ) => {
    const [showShadow, setShowShadow] = React.useState(false);

    const { handler: effectiveOnClose, closeButtonId = undefined } =
      'handler' in onClose ? onClose : { handler: onClose };

    // Allows backwards compatibility by rendering children as title content
    // if an explicit headingConfig isn't provided. Also similarly adds a
    // close button, automagically,
    if (!headingConfig) {
      headingConfig = {};
      if (children && !disableTypography) {
        headingConfig.left = {
          // TODO: Remove the children assertion by tracking down cases where
          // supplied children aren't showing as 'string', and making corrections
          configType: 'title',
          title: children as string,
        };
      }
      if (effectiveOnClose) {
        headingConfig.right = {
          configType: 'closeButton',
          id: closeButtonId,
        };
      }
    }

    React.useEffect(() => {
      if (!showShadowWhenScrolled) {
        return;
      }

      const dialogContent = document.querySelector('[data-omakase-dialog-content="true"]');

      function onScrollEvent(event: Event) {
        if (!dialogContent) {
          return;
        }
        if (!showShadow) {
          setShowShadow(true);
        }
        if (dialogContent.scrollTop <= 0) {
          setShowShadow(false);
        }
      }

      const debouncedScrollListener = _.debounce(onScrollEvent, 400, { leading: true });

      if (dialogContent) {
        dialogContent.addEventListener('scroll', debouncedScrollListener);
      }

      return () => {
        dialogContent && dialogContent.removeEventListener('scroll', debouncedScrollListener);
      };
    }, [showShadow, showShadowWhenScrolled]);

    const getHeadingElementLeft = () => {
      if (!headingConfig?.left) {
        return;
      }
      const { configType, id: leftId } = headingConfig.left;
      switch (configType) {
        case 'arrow':
          return (
            <IconButton
              css={dialogHeadingLeftElementArrow}
              aria-label="Back"
              component="button"
              onClick={headingConfig.left.onClickArrow ?? effectiveOnClose}
              size="medium"
              variant="ghost"
              type="button"
              id={leftId}
              data-testid="back-arrow"
            >
              <IconMigrationUtil legacyIcon={<IconArrowLeftLegacy />} newIcon={<IconArrowLeft />} />
            </IconButton>
          );
        case 'logo':
          return <TockLogo applyLeftHeadingElementStyles id={leftId} />;
        case 'title':
          return (
            <Typography
              ref={ref}
              variant="heading04"
              component="h2"
              css={dialogHeadingLeftElementTitle}
              id={id}
            >
              {headingConfig.left.title}
            </Typography>
          );
        default:
          assertNever(configType);
      }
    };

    const getHeadingElementCenter = () => {
      if (!headingConfig?.center) {
        return;
      }
      const { configType } = headingConfig.center;
      switch (configType) {
        case 'title':
          return (
            <Typography
              ref={ref}
              variant="heading04"
              component="h2"
              css={dialogHeadingCenterElementTitle}
              id={id}
            >
              {headingConfig.center.title}
            </Typography>
          );
        case 'button':
          return headingConfig.center.button;
        default:
          assertNever(configType);
      }
    };

    const getHeadingElementRight = () => {
      if (!headingConfig?.right) {
        return;
      }
      const { configType, id: rightId } = headingConfig.right;
      switch (configType) {
        case 'closeButton':
          return (
            <IconButton
              css={dialogHeadingRightElement}
              component="button"
              size="medium"
              variant="ghost"
              aria-label="Close"
              onClick={effectiveOnClose}
              type="button"
              id={rightId}
              data-testid="close-button"
            >
              <IconMigrationUtil legacyIcon={<IconClose />} newIcon={<IconCrossLarge />} />
            </IconButton>
          );
        default:
          assertNever(configType);
      }
    };

    const elementLeft = getHeadingElementLeft();
    const elementCenter = getHeadingElementCenter();
    const elementRight = getHeadingElementRight();

    return (
      <MuiDialogTitle
        css={(theme: Theme) => [dialogHeading(theme), showShadow && dialogHeadingShadow(theme)]}
        data-testid="dialog__heading"
        disableTypography
        {...rest}
      >
        {disableTypography ? children : null}
        {elementLeft ? elementLeft : null}
        {elementCenter ? <div css={dialogHeadingCenterElement}>{elementCenter}</div> : null}
        {elementRight ? elementRight : null}
      </MuiDialogTitle>
    );
  }
);

export default DialogHeading;
